/**
 * Audio Metadata Schema and Utilities
 *
 * This module defines the metadata schema for audio files and provides utilities
 * for creating, reading, and managing metadata JSON sidecar files.
 *
 * Metadata Schema:
 * {
 *   session_id: string - Unique identifier for the recording session
 *   patient_id: string - Patient database ID
 *   patient_name: string - Patient name for display
 *   audio_index: number - Sequential index number (1, 2, 3...)
 *   created_at: string - ISO timestamp
 *   duration: number - Recording duration in seconds
 *   ehr_id: string - EHR/medical record number
 *   schema_name: string - Database schema
 *   bucket: string - Storage bucket name
 *   storage_path: string - Full path in Supabase storage
 *   audio_url: string - Public URL of the audio file
 *   filename: string - Original filename
 * }
 */

// Metadata schema version for future compatibility
export const METADATA_VERSION = "1.0";
export const METADATA_FILENAME_SUFFIX = "_metadata.json";

/**
 * Create metadata object for an audio file
 * @param {Object} params - Metadata parameters
 * @returns {Object} Complete metadata object
 */
export const createAudioMetadata = ({
  sessionId,
  patientId,
  patientName,
  audioIndex,
  duration,
  ehrId,
  schemaName,
  bucket,
  storagePath,
  audioUrl,
  filename,
}) => {
  return {
    version: METADATA_VERSION,
    session_id: sessionId || generateSessionId(),
    patient_id: patientId,
    patient_name: patientName,
    audio_index: audioIndex || 1,
    created_at: new Date().toISOString(),
    duration: duration || 0,
    ehr_id: ehrId || "unknown_ehr",
    schema_name: schemaName || "public",
    bucket: bucket || "backup-audio",
    storage_path: storagePath || "",
    audio_url: audioUrl || "",
    filename: filename || "",
  };
};

/**
 * Generate a unique session ID for audio recordings
 * @returns {string} Unique session identifier
 */
export const generateSessionId = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `audio_${timestamp}_${randomPart}`;
};

/**
 * Generate a session_id from a filename for backward compatibility
 * @param {string} filename - Original filename
 * @returns {string} Session identifier derived from filename
 */
export const generateSessionIdFromFilename = (filename) => {
  if (!filename) return `audio_${Date.now().toString(36)}_unknown`;
  // Sanitize filename to create a valid session_id
  const sanitized = filename
    .replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "")
    .replace(/[^\w]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `audio_${sanitized}`;
};

/**
 * Determine if candidate audio should replace existing audio based on data completeness
 * Priority: audio_url > session_id > audio_index > patient_id > filename-only
 * @param {Object} existing - Existing audio object
 * @param {Object} candidate - Candidate audio object to potentially replace existing
 * @returns {boolean} True if candidate should replace existing
 */
export const shouldReplace = (existing, candidate) => {
  if (!existing || !candidate) return false;

  // Score each audio based on completeness (higher = more complete)
  const scoreAudio = (audio) => {
    let score = 0;
    if (audio.audio_url) score += 16; // Can be played
    if (audio.session_id && !audio.session_id.startsWith("audio_")) score += 8; // Has proper session_id (not filename-based)
    if (audio.session_id) score += 4; // Has any session_id
    if (audio.audio_index !== null && audio.audio_index !== undefined)
      score += 2; // Has audio index
    if (audio.patient_id) score += 1; // Has patient_id
    return score;
  };

  const existingScore = scoreAudio(existing);
  const candidateScore = scoreAudio(candidate);

  return candidateScore > existingScore;
};

/**
 * Deduplicate audio list by session_id
 * Keeps most complete version of each recording
 * @param {Array} audios - Array of audio objects
 * @returns {Array} Deduplicated array
 */
export const deduplicateBySessionId = (audios) => {
  if (!Array.isArray(audios)) return [];

  const sessionMap = new Map();
  for (const audio of audios) {
    // Generate session_id if not present (backward compatibility)
    const sessionId =
      audio.session_id ||
      generateSessionIdFromFilename(audio.filename || audio.name || "");

    const existing = sessionMap.get(sessionId);
    if (!existing) {
      sessionMap.set(sessionId, { ...audio, session_id: sessionId });
    } else {
      // Keep the one with more complete data
      if (shouldReplace(existing, audio)) {
        sessionMap.set(sessionId, { ...audio, session_id: sessionId });
      }
    }
  }
  return Array.from(sessionMap.values());
};

/**
 * Recorder / backup-of-live captions set `is_live_transcript`; Corti post-save often omits it.
 * Explicit `false` means Corti; restored / backup_restore rows resolve as live.
 */
export const transcriptShowsAsLive = (t) => {
  if (!t || typeof t !== "object") return false;
  if (t.is_live_transcript === true) return true;
  if (t.is_live_transcript === false) return false;
  if (t.restored_from_backup === true) return true;
  if (
    typeof t.audio_source === "string" &&
    t.audio_source === "backup_restore"
  )
    return true;
  return false;
};

const normalizeComparableAudioUrl = (u) =>
  typeof u === "string" ? u.trim().split("?")[0] : "";

/** YYYY-MM-DD from ISO date string or parseable timestamp. */
const toYyyyMmDd = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const lead = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (lead) return lead[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
};

/**
 * Visit folder date from a public storage URL or bucket-relative path.
 * Matches `{schema}/{year}/{YYYY-MM-DD}/` under `audio` or `backup-audio`.
 */
export const extractVisitDateFromAudioStorageRef = (urlOrPath) => {
  if (!urlOrPath || typeof urlOrPath !== "string") return null;
  const norm = urlOrPath.split("?")[0].replace(/\\/g, "/");
  const bucketMatch = norm.match(
    /\/(?:audio|backup-audio)\/[^/]+\/(\d{4})\/(\d{4}-\d{2}-\d{2})\//i,
  );
  if (bucketMatch && /^\d{4}-\d{2}-\d{2}$/.test(bucketMatch[2])) {
    return bucketMatch[2];
  }
  const pathMatch = norm.match(
    /(?:^|\/)(\d{4})\/(\d{4}-\d{2}-\d{2})\/[^/]+\.(?:webm|mp3|wav|m4a|ogg)/i,
  );
  if (pathMatch && /^\d{4}-\d{2}-\d{2}$/.test(pathMatch[2])) {
    return pathMatch[2];
  }
  return null;
};

/**
 * Which visit DAY a `more_audios` entry belongs to. Storage-path date wins
 * (it's written by the uploader and can't drift), entry timestamps second.
 * Exported so views that group by visit day use the same rule
 * filterAudiosForVisitDate() filters by.
 */
export const audioEntryVisitDate = (audio) => {
  if (!audio || typeof audio !== "object") return null;
  const fromUrl = extractVisitDateFromAudioStorageRef(
    audio.url || audio.audio_url || "",
  );
  if (fromUrl) return fromUrl;
  const fromPath = extractVisitDateFromAudioStorageRef(
    audio.storage_path || audio.path || "",
  );
  if (fromPath) return fromPath;
  for (const key of [
    "scheduledDate",
    "scheduled_date",
    "visit_date",
    "date_",
    "recording_date",
    "uploaded_at",
    "created_at",
    "recording_timestamp",
    "timestamp",
  ]) {
    const d = toYyyyMmDd(audio[key]);
    if (d) return d;
  }
  return null;
};

/**
 * Only audios provably tied to this visit day (storage path date or entry timestamps).
 * Does not include other dates or blanket backup-restore rows without a matching date.
 */
export const filterAudiosForVisitDate = (
  moreAudios,
  visitScheduleDate,
  _schemaName,
) => {
  if (!Array.isArray(moreAudios) || moreAudios.length === 0) return [];
  const visit = toYyyyMmDd(visitScheduleDate);
  if (!visit) return [];
  return moreAudios.filter((a) => audioEntryVisitDate(a) === visit);
};

/** Visit folder date from transcription-backups storage URL or JSON path. */
export const extractVisitDateFromTranscriptionBackupRef = (urlOrPath) => {
  if (!urlOrPath || typeof urlOrPath !== "string") return null;
  const norm = urlOrPath.split("?")[0].replace(/\\/g, "/");
  const bucketMatch = norm.match(
    /\/transcription-backups\/[^/]+\/(\d{4})\/(\d{4}-\d{2}-\d{2})\//i,
  );
  if (bucketMatch && /^\d{4}-\d{2}-\d{2}$/.test(bucketMatch[2])) {
    return bucketMatch[2];
  }
  const jsonPath = norm.match(/\/(\d{4})\/(\d{4}-\d{2}-\d{2})\/[^/]+\.json/i);
  if (jsonPath && /^\d{4}-\d{2}-\d{2}$/.test(jsonPath[2])) {
    return jsonPath[2];
  }
  return extractVisitDateFromAudioStorageRef(urlOrPath);
};

const transcriptEntryVisitDate = (transcript) => {
  if (!transcript || typeof transcript !== "object") return null;
  const fromAudioUrl = extractVisitDateFromAudioStorageRef(
    transcript.audio_url || "",
  );
  if (fromAudioUrl) return fromAudioUrl;
  const fromBackup = extractVisitDateFromTranscriptionBackupRef(
    transcript.filename ||
      transcript.storage_path ||
      transcript.backup_path ||
      "",
  );
  if (fromBackup) return fromBackup;
  const meta = transcript.metadata;
  if (meta && typeof meta === "object") {
    for (const key of [
      "date_of_service",
      "scheduledDate",
      "scheduled_date",
      "visit_date",
      "date_",
    ]) {
      const d = toYyyyMmDd(meta[key]);
      if (d) return d;
    }
    const fromMetaPath = extractVisitDateFromTranscriptionBackupRef(
      meta.storage_path || meta.path || "",
    );
    if (fromMetaPath) return fromMetaPath;
  }
  for (const key of [
    "date_of_service",
    "scheduledDate",
    "scheduled_date",
    "visit_date",
    "date_",
  ]) {
    const d = toYyyyMmDd(transcript[key]);
    if (d) return d;
  }
  return null;
};

/**
 * True if a transcript row belongs to this `more_audios` (or backup) audio row.
 * Order: session_id → normalized audio_url → filename / display labels.
 */
export const transcriptBelongsToAudio = (audio, transcript) => {
  if (!audio || !transcript) return false;

  const audioSessionId = audio.session_id;
  if (audioSessionId) {
    const tSid = transcript.session_id || transcript.metadata?.session_id;
    if (tSid && String(tSid) === String(audioSessionId)) return true;
  }

  const au = normalizeComparableAudioUrl(audio.url || audio.audio_url);
  const tu = normalizeComparableAudioUrl(transcript.audio_url);
  if (au && tu && au === tu) return true;

  const audioFilename = audio.filename || audio.name || "";
  const tFilename = transcript.filename || transcript.audio_file_name || "";
  if (audioFilename && tFilename) {
    const audioBase = audioFilename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
    const tBase = tFilename
      .replace(/\.json$/i, "")
      .replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
    if (
      audioBase === tBase ||
      audioBase.includes(tBase) ||
      tBase.includes(audioBase)
    ) {
      return true;
    }
  }

  if (
    transcript.audio_file_name &&
    audio.name &&
    transcript.audio_file_name === audio.name
  ) {
    return true;
  }
  if (
    transcript.audio_file_name &&
    audio.displayName &&
    transcript.audio_file_name === audio.displayName
  ) {
    return true;
  }

  return false;
};

/**
 * Transcripts for this visit day (storage path, metadata, or linked visit audio).
 * Includes AI recap rows only when their linked transcript is included.
 */
export const filterTranscriptsForVisitDate = (
  transcripts,
  visitScheduleDate,
  moreAudios = [],
  schemaName,
) => {
  if (!Array.isArray(transcripts) || transcripts.length === 0) return [];
  const visit = toYyyyMmDd(visitScheduleDate);
  if (!visit) return [];
  const visitAudios = filterAudiosForVisitDate(
    moreAudios,
    visitScheduleDate,
    schemaName,
  );

  const matchesVisit = (t) => {
    if (!t || typeof t !== "object" || t.is_ai_recap) return false;
    const entryDate = transcriptEntryVisitDate(t);
    if (entryDate) return entryDate === visit;
    if (visitAudios.length > 0) {
      return visitAudios.some((a) => transcriptBelongsToAudio(a, t));
    }
    return false;
  };

  const filtered = transcripts.filter(matchesVisit);
  const linkedIds = new Set(filtered.map((t) => t.id).filter(Boolean));
  const withRecaps = [...filtered];
  for (const t of transcripts) {
    if (
      t?.is_ai_recap &&
      t.linked_transcript_id &&
      linkedIds.has(t.linked_transcript_id)
    ) {
      withRecaps.push(t);
    }
  }
  return withRecaps.sort(
    (a, b) => new Date(a?.created_at || 0) - new Date(b?.created_at || 0),
  );
};

/**
 * Match transcriptions to an audio based on session_id
 * Primary: session_id match
 * Secondary: filename match (for backward compatibility)
 * @param {Object} audio - Audio object
 * @param {Array} transcriptions - Array of transcription objects
 * @returns {Object|null} Matched transcription or null
 */
export const matchTranscriptionToAudio = (audio, transcriptions) => {
  if (!audio || !Array.isArray(transcriptions) || transcriptions.length === 0) {
    return null;
  }
  return (
    transcriptions.find((t) => transcriptBelongsToAudio(audio, t)) ?? null
  );
};

/**
 * Group backups by session_id for unified display
 * Each group represents one logical recording with potential:
 * - Primary audio file (in `audio` bucket)
 * - Audio backup file (in `backup-audio` bucket)
 * - Transcription JSON (in `transcription-backups` bucket)
 * @param {Array} audioBackups - Array of audio backup objects
 * @param {Array} transcriptionBackups - Array of transcription backup objects
 * @returns {Array} Grouped backups array
 */
export const groupBackupsBySessionId = (audioBackups, transcriptionBackups) => {
  if (!Array.isArray(audioBackups)) audioBackups = [];
  if (!Array.isArray(transcriptionBackups)) transcriptionBackups = [];

  const sessionMap = new Map();

  // Process audio backups first
  for (const audio of audioBackups) {
    const sessionId =
      audio.session_id ||
      generateSessionIdFromFilename(audio.file_name || audio.name || "");
    if (!sessionMap.has(sessionId)) {
      sessionMap.set(sessionId, {
        session_id: sessionId,
        audio_backup: null,
        transcription_backup: null,
        has_audio: false,
        has_transcript: false,
      });
    }
    const group = sessionMap.get(sessionId);
    group.audio_backup = audio;
    group.has_audio = true;
    if (audio.metadata?.session_id) {
      group.session_id = audio.metadata.session_id;
    }
  }

  // Process transcription backups
  for (const transcript of transcriptionBackups) {
    const sessionId =
      transcript.session_id ||
      transcript.metadata?.session_id ||
      generateSessionIdFromFilename(transcript.file_name || "");
    if (!sessionMap.has(sessionId)) {
      sessionMap.set(sessionId, {
        session_id: sessionId,
        audio_backup: null,
        transcription_backup: null,
        has_audio: false,
        has_transcript: false,
      });
    }
    const group = sessionMap.get(sessionId);
    group.transcription_backup = transcript;
    group.has_transcript = true;
  }

  return Array.from(sessionMap.values());
};

/**
 * Get metadata filename from audio filename
 * @param {string} audioFilename - Original audio filename
 * @returns {string} Metadata filename
 */
export const getMetadataFilename = (audioFilename) => {
  if (!audioFilename) return "";
  // Replace audio extension with metadata suffix
  return (
    audioFilename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "") +
    METADATA_FILENAME_SUFFIX
  );
};

/**
 * Get audio filename from metadata filename
 * @param {string} metadataFilename - Metadata filename
 * @returns {string} Audio filename
 */
export const getAudioFilenameFromMetadata = (metadataFilename) => {
  if (!metadataFilename) return "";
  return metadataFilename.replace(METADATA_FILENAME_SUFFIX, "");
};

/**
 * Check if a filename is a metadata file
 * @param {string} filename - Filename to check
 * @returns {boolean} True if filename is a metadata file
 */
export const isMetadataFile = (filename) => {
  if (!filename) return false;
  return filename.endsWith(METADATA_FILENAME_SUFFIX);
};

/**
 * Parse audio index from filename (for backward compatibility)
 * Looks for patterns like _BackupN_, _RecordingN_, _AudioN_; also `_BackupN` / `_AudioN`
 * at end of stem after stripping optional UUID tail (matches backup↔transcript pairing).
 * Uses the same UUID-tail pattern as backup-audio object keys.
 * @param {string} filename - Filename to parse
 * @returns {number|null} Audio index or null if not found
 */
const BACKUP_AUDIO_UUID_TAIL_RE =
  /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const parseAudioIndexFromFilename = (filename) => {
  if (!filename) return null;

  const backupMatch = filename.match(/_Backup(\d+)_/i);
  if (backupMatch) return parseInt(backupMatch[1], 10);

  const recordingMatch = filename.match(/_Recording(\d+)_/i);
  if (recordingMatch) return parseInt(recordingMatch[1], 10);

  const audioMatch = filename.match(/_Audio(\d+)/i);
  if (audioMatch) return parseInt(audioMatch[1], 10);

  // Same stem rules as backup↔transcript pairing: strip optional UUID tail, then `_BackupN` / `_AudioN` at end.
  const stem = filename
    .replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "")
    .replace(BACKUP_AUDIO_UUID_TAIL_RE, "");
  const backupEnd = /_Backup(\d+)$/i.exec(stem);
  if (backupEnd) return parseInt(backupEnd[1], 10);
  const audioEnd = /_Audio(\d+)$/i.exec(stem);
  if (audioEnd) return parseInt(audioEnd[1], 10);

  return null;
};

const toClinicalSlotNumber = (value) => {
  if (value == null || value === "") return null;
  const n =
    typeof value === "number" && Number.isFinite(value)
      ? value
      : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
};

/**
 * Clinical audio slot (1-based) from backup sidecar JSON plus the uploaded media object's filename.
 * When sidecar and path disagree, trusts filename / `_BackupN` stem (pairs with transcription `_AudioN.json`).
 * @param {Object|null} embeddedSidecar — Parsed `_metadata.json` or similar
 * @param {string|null} mediaFilename — e.g. `Name-EHR_Backup2_<uuid>.webm`
 * @returns {number|null}
 */
export const resolveClinicalAudioSlotFromBackupSidecarAndFilename = (
  embeddedSidecar,
  mediaFilename,
) => {
  const fromEmbedded =
    embeddedSidecar != null &&
    typeof embeddedSidecar === "object" &&
    !Array.isArray(embeddedSidecar)
      ? toClinicalSlotNumber(
          embeddedSidecar.audio_number ??
            embeddedSidecar.audioNumber ??
            embeddedSidecar.metadata?.audio_number ??
            embeddedSidecar.metadata?.audioNumber ??
            embeddedSidecar.metadata?.audio_index ??
            embeddedSidecar.audio_index,
        )
      : null;

  const fromParser = mediaFilename
    ? toClinicalSlotNumber(parseAudioIndexFromFilename(mediaFilename))
    : null;

  let slotFromStem = null;
  if (mediaFilename) {
    const stemNoExt = mediaFilename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
    const stemNoUuid = stemNoExt.replace(BACKUP_AUDIO_UUID_TAIL_RE, "");
    const stemMatch = /_Backup(\d+)$/i.exec(stemNoUuid);
    if (stemMatch) slotFromStem = toClinicalSlotNumber(stemMatch[1]);
  }

  const fileDerived = slotFromStem != null ? slotFromStem : fromParser;

  if (
    fileDerived != null &&
    fromEmbedded != null &&
    fileDerived !== fromEmbedded
  ) {
    return fileDerived;
  }
  if (fromEmbedded != null) return fromEmbedded;
  if (fileDerived != null) return fileDerived;

  return null;
};

/**
 * True when backup-audio filename pairs with transcription-backup JSON naming:
 * `Name-EHR_BackupN_<uuid>.webm` ↔ `Name-EHR_AudioN.json`.
 * Rolling periodic upserts: `Name-EHR_Rolling_<slug>.webm` ↔ same prefix + `_Audio{clinicalN}.json`.
 * Also preserves legacy substring / exact-stem equality checks.
 */
export const backupAudioFilenamePairsTranscriptFile = (
  audioFilename,
  transcriptFilename,
) => {
  if (!audioFilename || !transcriptFilename) return false;

  const baseAudio = audioFilename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
  const baseTranscript = transcriptFilename.replace(/\.json$/i, "");

  const audioStem = baseAudio.replace(BACKUP_AUDIO_UUID_TAIL_RE, "");
  const bm = /^(.+)_Backup(\d+)$/i.exec(audioStem);
  const tm = /^(.+)_Audio(\d+)$/i.exec(baseTranscript);
  if (
    bm &&
    tm &&
    bm[1] === tm[1] &&
    String(bm[2]) === String(tm[2])
  ) {
    return true;
  }

  const rollingA = /^(.+)_Rolling_.+$/i.exec(audioStem);
  if (
    rollingA &&
    tm &&
    rollingA[1] === tm[1]
  ) {
    return true;
  }

  return (
    baseAudio === baseTranscript ||
    baseTranscript.includes(baseAudio) ||
    baseAudio.includes(baseTranscript)
  );
};

/**
 * Uploaded object keys sometimes contain spaces or `%20`; transcript/copilot sidecars
 * use the same sanitization as backup uploads (spaces → underscores). Normalize before
 * matching `_AudioN_` / `_BackupN` patterns so inferred paths match saved JSON keys.
 */
const normalizeAudioStemForSidecarMatching = (stem) => {
  if (!stem || typeof stem !== "string") return "";
  let s = stem.replace(/%20/gi, " ");
  s = s.replace(/\s+/g, "_");
  s = s.replace(/_+/g, "_");
  return s;
};

/**
 * Infer `schema/year/date/Prefix_AudioN.json` path in transcription-backups
 * from a backup-audio storage path `{schema}/{year}/{date}/Prefix_BackupN_<uuid>.ext`
 * or rolling periodic `{schema}/{year}/{date}/Prefix_Rolling_<slug>.ext` when `hints` includes the clinical slot,
 * or direct/clinical upload `{schema}/{year}/{date}/Prefix_AudioN_<timestamp>.ext`.
 */
export const guessTranscriptionJsonPathFromBackupAudioStoragePath = (
  audioStoragePath,
  hints = {},
) => {
  if (!audioStoragePath || typeof audioStoragePath !== "string") return "";
  const slotRaw =
    hints.clinicalAudioSlot ?? hints.audioSlot ?? hints.audio_number;
  const norm = audioStoragePath.replace(/^\/+|\/+$/g, "").replace(/\\/g, "/");
  const slash = norm.lastIndexOf("/");
  if (slash < 0) return "";
  const dirname = norm.slice(0, slash);
  const basename = norm.slice(slash + 1);
  const stem = basename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
  const audioStem = normalizeAudioStemForSidecarMatching(
    stem.replace(BACKUP_AUDIO_UUID_TAIL_RE, ""),
  );

  const rollingM = /^(.+)_Rolling_.+$/i.exec(audioStem);
  if (
    rollingM &&
    slotRaw != null &&
    slotRaw !== ""
  ) {
    const sn = Number(slotRaw);
    if (Number.isFinite(sn) && sn >= 1) {
      return `${dirname}/${rollingM[1]}_Audio${sn}.json`;
    }
  }

  /** Final clinical upload / external window: `{prefix}_Audio{N}_{isoTimestamp}.webm` */
  const directAudio = /^(.*)_Audio(\d+)_/i.exec(audioStem);
  if (directAudio) {
    const prefix = directAudio[1];
    const num = directAudio[2];
    return `${dirname}/${prefix}_Audio${num}.json`;
  }

  const m = /^(.+)_Backup(\d+)$/i.exec(audioStem);
  if (!m) return "";
  const prefix = m[1];
  const num = m[2];
  return `${dirname}/${prefix}_Audio${num}.json`;
};

/**
 * Same folder / stem as transcript JSON, with `_copilot.json` suffix:
 * `{schema}/{year}/{date}/Prefix_AudioN_copilot.json`
 */
export const guessCopilotJsonStoragePathFromBackupAudioStoragePath = (
  audioStoragePath,
  hints = {},
) => {
  const transcriptRel =
    guessTranscriptionJsonPathFromBackupAudioStoragePath(
      audioStoragePath,
      hints,
    );
  if (!transcriptRel) return "";
  return transcriptRel.replace(/\.json$/i, "_copilot.json");
};

/**
 * Relative copilot keys to try for this audio path (order matters).
 * External recordings upload `{name}{-ehr}_AudioN_<ts>.webm` but the backend writes
 * `{sanitized}-{ehr}_AudioN_copilot.json`; when EHR is empty that is `{name}-_AudioN_…`
 * while the audio filename has no hyphen before `_AudioN`.
 */
export const guessCopilotJsonStoragePathCandidatesFromBackupAudioStoragePath = (
  audioStoragePath,
  hints = {},
) => {
  const primary = guessCopilotJsonStoragePathFromBackupAudioStoragePath(
    audioStoragePath,
    hints,
  );
  if (!audioStoragePath || typeof audioStoragePath !== "string") {
    return primary ? [primary] : [];
  }
  const norm = audioStoragePath.replace(/^\/+|\/+$/g, "").replace(/\\/g, "/");
  const slash = norm.lastIndexOf("/");
  if (slash < 0) return primary ? [primary] : [];

  const dirname = norm.slice(0, slash);
  const basename = norm.slice(slash + 1);
  const stem = basename.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
  const audioStem = normalizeAudioStemForSidecarMatching(
    stem.replace(BACKUP_AUDIO_UUID_TAIL_RE, ""),
  );

  const directAudio = /^(.*)_Audio(\d+)_/i.exec(audioStem);
  if (directAudio) {
    const prefix = directAudio[1];
    const num = directAudio[2];
    const backendEmptyEhrShape = `${dirname}/${prefix}-_Audio${num}_copilot.json`;
    const audioFilenameShape = `${dirname}/${prefix}_Audio${num}_copilot.json`;
    const out = [];
    const push = (p) => {
      if (p && !out.includes(p)) out.push(p);
    };
    push(backendEmptyEhrShape);
    push(audioFilenameShape);
    if (/-unknown_ehr$/i.test(prefix)) {
      const shortPrefix = prefix.replace(/-unknown_ehr$/i, "");
      if (shortPrefix && shortPrefix !== prefix) {
        push(`${dirname}/${shortPrefix}-_Audio${num}_copilot.json`);
        push(`${dirname}/${shortPrefix}_Audio${num}_copilot.json`);
      }
      const resolvedEhr = normalizeCopilotEhrHint(hints.resolvedCopilotEhr);
      if (resolvedEhr && shortPrefix) {
        push(
          `${dirname}/${shortPrefix}-${resolvedEhr}_Audio${num}_copilot.json`,
        );
      }
    }
    return out;
  }

  const directAudioNoTrail = /^(.*)_Audio(\d+)$/i.exec(audioStem);
  if (directAudioNoTrail) {
    const prefix = directAudioNoTrail[1];
    const num = directAudioNoTrail[2];
    const backendEmptyEhrShape = `${dirname}/${prefix}-_Audio${num}_copilot.json`;
    const audioFilenameShape = `${dirname}/${prefix}_Audio${num}_copilot.json`;
    const out = [];
    const push = (p) => {
      if (p && !out.includes(p)) out.push(p);
    };
    push(backendEmptyEhrShape);
    push(audioFilenameShape);
    if (/-unknown_ehr$/i.test(prefix)) {
      const shortPrefix = prefix.replace(/-unknown_ehr$/i, "");
      if (shortPrefix && shortPrefix !== prefix) {
        push(`${dirname}/${shortPrefix}-_Audio${num}_copilot.json`);
        push(`${dirname}/${shortPrefix}_Audio${num}_copilot.json`);
      }
      const resolvedEhr = normalizeCopilotEhrHint(hints.resolvedCopilotEhr);
      if (resolvedEhr && shortPrefix) {
        push(
          `${dirname}/${shortPrefix}-${resolvedEhr}_Audio${num}_copilot.json`,
        );
      }
    }
    return out;
  }

  return primary ? [primary] : [];
};

function normalizeCopilotEhrHint(ehr) {
  if (ehr == null) return "";
  const s = String(ehr).trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (low === "unknown_ehr" || low === "unknown") return "";
  return s;
}

/** Extract `{bucketPath}/{withoutLeadingSlash}` from a Supabase public object URL. */
export const extractStorageObjectPathFromPublicUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const stripped = url.split("?")[0];
  const m = stripped.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/i);
  return m ? decodeURIComponent(m[1]) : "";
};

/**
 * Fetch metadata from a JSON file URL
 * @param {string} metadataUrl - URL to the metadata JSON file
 * @returns {Promise<Object|null>} Parsed metadata or null if fetch fails
 */
export const fetchMetadataFromUrl = async (metadataUrl) => {
  if (!metadataUrl) return null;

  try {
    const response = await fetch(metadataUrl);
    if (!response.ok) return null;

    const metadata = await response.json();
    return metadata;
  } catch (error) {
    console.warn("[AudioMetadata] Failed to fetch metadata:", error);
    return null;
  }
};

/**
 * Fetch metadata alongside audio file listing
 * When listing audio files, this function attempts to find corresponding metadata files
 * @param {string} audioFilename - Audio filename
 * @param {string} audioFolderPath - Folder path where audio is stored
 * @param {string} bucket - Storage bucket name
 * @param {Object} supabase - Supabase client
 * @returns {Promise<Object|null>} Metadata object if found
 */
export const fetchAudioMetadata = async (
  audioFilename,
  audioFolderPath,
  bucket,
  supabase,
) => {
  if (!audioFilename || !audioFolderPath || !supabase) return null;

  const metadataFilename = getMetadataFilename(audioFilename);
  const metadataPath = audioFolderPath
    ? `${audioFolderPath}/${metadataFilename}`
    : metadataFilename;

  try {
    const normalizedPath = metadataPath.replace(/^\/+|\/+$/g, "");
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(normalizedPath);

    if (error || !data) return null;

    const text = await data.text();
    const metadata = JSON.parse(text);
    return metadata;
  } catch (error) {
    console.warn("[AudioMetadata] Failed to fetch audio metadata:", error);
    return null;
  }
};

/**
 * Build a complete audio metadata object by merging multiple sources:
 * 1. Embedded metadata (from fetch or parse)
 * 2. Filename-based extraction (for backward compatibility)
 * 3. Additional context data
 *
 * @param {Object} embeddedMetadata - Metadata read from JSON file (may be null)
 * @param {string} filename - Audio filename for backward compatibility parsing
 * @param {Object} contextData - Additional context (patient_id, bucket, folder, etc.)
 * @returns {Object} Complete metadata with backward compatibility
 */
export const mergeAudioMetadata = (
  embeddedMetadata,
  filename,
  contextData = {},
) => {
  // Start with embedded metadata if available
  const metadata = {
    ...embeddedMetadata,
  };

  // Add backward compatibility fields
  metadata.session_id =
    metadata.session_id || contextData.sessionId || `audio_${filename}`;
  metadata.patient_id = metadata.patient_id || contextData.patientId || null;
  metadata.patient_name =
    metadata.patient_name || contextData.patientName || "Unknown";

  // Parse audio_index from metadata or filename
  metadata.audio_index =
    metadata.audio_index ||
    parseAudioIndexFromFilename(filename) ||
    contextData.audioIndex ||
    null;

  // Add bucket and storage info
  metadata.bucket = metadata.bucket || contextData.bucket || "backup-audio";
  metadata.storage_path =
    metadata.storage_path || contextData.storagePath || "";

  // Add schema
  metadata.schema_name =
    metadata.schema_name || contextData.schemaName || "public";

  return metadata;
};

/**
 * Create metadata JSON string for upload
 * @param {Object} metadata - Metadata object
 * @returns {Blob} JSON blob for upload
 */
export const createMetadataBlob = (metadata) => {
  const jsonString = JSON.stringify(metadata, null, 2);
  return new Blob([jsonString], { type: "application/json" });
};

export default {
  METADATA_VERSION,
  METADATA_FILENAME_SUFFIX,
  createAudioMetadata,
  generateSessionId,
  generateSessionIdFromFilename,
  shouldReplace,
  deduplicateBySessionId,
  extractVisitDateFromAudioStorageRef,
  extractVisitDateFromTranscriptionBackupRef,
  filterAudiosForVisitDate,
  filterTranscriptsForVisitDate,
  transcriptShowsAsLive,
  transcriptBelongsToAudio,
  matchTranscriptionToAudio,
  groupBackupsBySessionId,
  getMetadataFilename,
  getAudioFilenameFromMetadata,
  isMetadataFile,
  parseAudioIndexFromFilename,
  backupAudioFilenamePairsTranscriptFile,
  resolveClinicalAudioSlotFromBackupSidecarAndFilename,
  guessTranscriptionJsonPathFromBackupAudioStoragePath,
  fetchMetadataFromUrl,
  fetchAudioMetadata,
  mergeAudioMetadata,
  createMetadataBlob,
};
